import type { FilePurpose } from '../api/business/types';
import type { MessageKey } from '../i18n/sk';

/** The API's limit on one download or attachment (R0030 WP-10). */
export const MAX_FILE_MB = 50;

/** The names the API takes for each purpose — it judges the bytes again; this only spares a useless upload. */
const EXTENSIONS: Record<FilePurpose, Array<string>> = {
  ATTACHMENT: [
    'pdf',
    'zip',
    'jpg',
    'jpeg',
    'png',
    'webp',
    'gif',
    'txt',
    'csv',
    'md',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'ppt',
    'pptx',
    'odt',
    'ods',
    'odp',
  ],
  DOWNLOAD: [
    'pdf',
    'zip',
    'jpg',
    'jpeg',
    'png',
    'webp',
    'gif',
    'txt',
    'csv',
    'md',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'ppt',
    'pptx',
    'odt',
    'ods',
    'odp',
    'epub',
    'mp3',
    'm4a',
    'mp4',
    'm4v',
  ],
};

/** What the file picker offers for a purpose. */
export const acceptedFor = (purpose: FilePurpose): string =>
  EXTENSIONS[purpose].map((ext) => `.${ext}`).join(',');

/** Why a chosen file cannot be uploaded for `purpose` — too large, empty or of a refused type — or null. */
export const fileProblem = (
  file: { name: string; size: number },
  purpose: FilePurpose,
): MessageKey | null => {
  const extension = /\.([A-Za-z0-9]+)$/.exec(file.name)?.[1]?.toLowerCase() ?? '';

  if (!EXTENSIONS[purpose].includes(extension)) {
    return 'files.typeRefused';
  }

  return file.size <= 0 || file.size > MAX_FILE_MB * 1024 * 1024 ? 'files.tooLarge' : null;
};

/** `ids` with the one at `index` moved by `step` (−1 up, 1 down); unchanged at either end. */
export const movedId = (ids: Array<string>, index: number, step: -1 | 1): Array<string> => {
  const target = index + step;

  if (index < 0 || index >= ids.length || target < 0 || target >= ids.length) {
    return ids;
  }

  const moved = [...ids];

  [moved[index], moved[target]] = [moved[target], moved[index]];

  return moved;
};

/** Texts as the form edits them, by language ('' where none). */
export const textsOf = (
  texts: Record<string, string | null>,
  languages: Array<string>,
): Record<string, string> => Object.fromEntries(languages.map((code) => [code, texts[code] ?? '']));

/** The texts a change sends: only what changed, an emptied one as null. */
export const changedTexts = (
  draft: Record<string, string>,
  stored: Record<string, string | null>,
): Record<string, string | null> =>
  Object.fromEntries(
    Object.entries(draft)
      .filter(([code, text]) => text.trim() !== (stored[code] ?? ''))
      .map(([code, text]) => [code, text.trim() === '' ? null : text.trim()]),
  );

/** A limit typed: '' none, else a whole number from 1 to `maximum`; undefined when neither. */
export const parseLimit = (text: string, maximum: number): number | null | undefined => {
  if (text.trim() === '') return null;

  const value = Number(text.trim());

  return /^\d+$/.test(text.trim()) && value >= 1 && value <= maximum ? value : undefined;
};
